/* eslint-disable */
import React, { useRef } from 'react';
import closeSvg from 'src/assets/close.svg';
import ReactModal from 'react-modal';
import {
  CloseIcon,
  Row,
  Label,
  Value,
  ValueGroup,
  Container,
  TitleValue,
  Heading,
  Separator,
} from './expense-history-styles';

const formatDate = (date?: string | Date | null) => {
  if (!date) return '-';

  const value = date instanceof Date ? date.toISOString() : date;

  // Preserve date-only values without timezone conversion
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${Number(month)}/${Number(day)}/${year}`;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) return '-';

  return parsedDate.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const getChangedFields = (before: any = {}, after: any = {}) => {
  const changes: Array<{
    field: string;
    before: any;
    after: any;
  }> = [];

  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);

  keys.forEach(key => {
    const beforeValue = before?.[key];
    const afterValue = after?.[key];

    // Arrays (receipts, attachments, etc.)
    if (Array.isArray(beforeValue) || Array.isArray(afterValue)) {
      const beforeJson = JSON.stringify(beforeValue || []);
      const afterJson = JSON.stringify(afterValue || []);

      if (beforeJson !== afterJson) {
        changes.push({
          field: key,
          before: beforeValue,
          after: afterValue,
        });
      }

      return;
    }

    // Nested objects
    if (beforeValue && afterValue && typeof beforeValue === 'object' && typeof afterValue === 'object') {
      const beforeJson = JSON.stringify(beforeValue);
      const afterJson = JSON.stringify(afterValue);

      if (beforeJson !== afterJson) {
        changes.push({
          field: key,
          before: beforeValue,
          after: afterValue,
        });
      }

      return;
    }

    // Primitive values
    if (beforeValue !== afterValue) {
      changes.push({
        field: key,
        before: beforeValue,
        after: afterValue,
      });
    }
  });

  return changes;
};

const formatFieldLabel = (field: string) => {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
    .trim();
};

export const ExpenseHistoryModal: React.FC<{
  isOpen: boolean;
  setIsOpen: Function;
  data: any; // optional because first time it's null
}> = ({ isOpen, setIsOpen, data = null }) => {
  const modalRef = useRef(null);

  const handleModalClose = () => {
    setIsOpen(false);
  };

  const isDateString = (value: any) => {
    if (typeof value !== 'string') return false;

    // Avoid treating simple numbers like "3" as dates
    if (/^\d+$/.test(value)) return false;

    return !isNaN(Date.parse(value));
  };

  const formatAmount = (value?: number) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toFixed(2);
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return '-';

    // ✅ Handle dates FIRST
    if (isDateString(value)) {
      return formatDate(value);
    }

    // ✅ Handle numbers
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return String(value);
  };

  const amountFields = ['totalAmount', 'billRate', 'payRate'];

  const formatFieldValue = (field: string, value: any) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const currency = data?.details?.currency || data?.details?.after?.currency || data?.details?.before?.currency;

    // Arrays: receipts and attachments
    if (Array.isArray(value)) {
      return formatArrayValue(field, value);
    }

    // Nested objects
    if (typeof value === 'object') {
      return '[Updated]';
    }

    // Normalize date fields regardless of backend format
    const dateFields = ['periodStart', 'periodEnd', 'invoiceDate', 'weekStart', 'weekEnd'];

    if (dateFields.includes(field)) {
      return formatDate(value);
    }

    // Normalize status capitalization
    if (field.toLowerCase() === 'status') {
      return String(value).toUpperCase();
    }

    // Format monetary fields
    if (amountFields.includes(field)) {
      const amount = Number(value);

      if (!Number.isFinite(amount)) return String(value);

      return `${currency ?? ''} ${amount.toFixed(2)}`.trim();
    }

    return formatValue(value);
  };

  const formatArrayValue = (field: string, value: any[]) => {
    if (!Array.isArray(value) || value.length === 0) {
      return '-';
    }

    if (field === 'receipts') {
      return `${value.length} receipt(s)`;
    }

    if (field === 'attachments') {
      return `${value.length} attachment(s)`;
    }

    return `${value.length} item(s)`;
  };

  const changedFields = getChangedFields(data?.details?.before, data?.details?.after);

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          overflowX: 'auto',
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: '0px auto',
          width: 'max-content',
          minWidth: '550px',
          maxWidth: '720px',
          height: 'max-content',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },
      }}
      onRequestClose={handleModalClose}
      // eslint-disable-next-line no-return-assign
      contentRef={(node: any) => (modalRef.current = node)}
    >
      <CloseIcon src={closeSvg} onClick={handleModalClose} />

      <>
        {data?.actionType === 'CREATED' ? (
          <Container>
            {/* Assignment */}
            <Heading>Details</Heading>
            <Separator />
            <Row>
              <Label>Assignment</Label>
              <ValueGroup>
                <Value>Assignment ID - {data.details?.assignmentId}</Value>
                <Value>Job Title - {data.details?.jobTitle}</Value>
                <Value>Hire Type - {data.details?.hireType}</Value>
              </ValueGroup>
            </Row>
            {/* Company */}
            <Row>
              <Label>Company</Label>
              <ValueGroup>
                <Value>{data.details?.companyName}</Value>
              </ValueGroup>
            </Row>

            {/* Client */}
            <Row>
              <Label>Client</Label>
              <ValueGroup>
                <Value>{data.details?.clientName}</Value>
              </ValueGroup>
            </Row>

            {/* Worker */}
            <Row>
              <Label>Worker</Label>
              <ValueGroup>
                <Value>{data.expense?.employeeName}</Value>
              </ValueGroup>
            </Row>

            {/* Approvers */}
            <Row>
              <Label>Approvers</Label>
              <ValueGroup>
                <Value>{data?.details?.approverName}</Value>
                <Value>{data?.details?.coApproverName}</Value>
              </ValueGroup>
            </Row>

            {/* Amounts */}
            <Row>
              <Label>Amounts</Label>
              <ValueGroup>
                <Value>
                  Total Amount - {data?.details?.currency} {formatAmount(data?.details?.totalAmount)}
                </Value>
              </ValueGroup>
            </Row>

            {/* Rate Info */}
            <Row>
              <Label>Rate Info</Label>
              <ValueGroup>
                <Value>Effective Date - {data?.details?.effectiveDate}</Value>

                <TitleValue>Standard</TitleValue>
                <Value>
                  Bill Rate - {data?.details?.currency} {data?.details?.billRate}
                </Value>
                <Value>
                  Pay Rate - {data?.details?.currency} {data?.details?.payRate}
                </Value>
                <Value>Markup - {data?.details?.markupPercent}%</Value>
              </ValueGroup>
            </Row>

            {/* Expense Dates */}
            <Row>
              <Label>Expense Start Date</Label>
              <ValueGroup>
                <Value>{data?.details?.weekStart}</Value>
              </ValueGroup>
            </Row>

            <Row>
              <Label>Expense End Date</Label>
              <ValueGroup>
                <Value>{data?.details?.weekEnd}</Value>
              </ValueGroup>
            </Row>

            {/* Attachments */}
            {/* <Row>
              <Label>Attachments</Label>
              <ValueGroup>
                {data?.expense?.attachments?.map((attachment: any) => (
                  <Value key={attachment.id}>{attachment.attachmentName}</Value>
                ))}
              </ValueGroup>
            </Row> */}

            <Row>
              <Label>Version</Label>
              <ValueGroup>
                <Value>{data?.details?.version}</Value>
              </ValueGroup>
            </Row>

            <Row>
              <Label>Status</Label>
              <ValueGroup>
                <Value>{data?.details?.status}</Value>
              </ValueGroup>
            </Row>

            <Row>
              <Label>Submitted At</Label>
              <ValueGroup>
                <Value>{formatDate(data?.details?.createdAt)}</Value>
              </ValueGroup>
            </Row>
          </Container>
        ) : data?.actionType === 'STATUS_CHANGE' ? (
          <Container>
            <Heading>Details</Heading>
            <Separator />

            <Heading>From status change</Heading>

            <Row>
              <Label>Status</Label>
              <ValueGroup>
                <Value>{data?.details?.before?.status ?? '-'}</Value>
              </ValueGroup>
            </Row>

            <Separator />

            <Heading>To</Heading>

            <Row>
              <Label>Status</Label>
              <ValueGroup>
                <Value>{data?.details?.after?.status ?? '-'}</Value>
              </ValueGroup>
            </Row>

            <Row>
              <Label>Changed By</Label>
              <ValueGroup>
                <Value>{data?.details?.after?.['Changed by'] ?? '-'}</Value>
              </ValueGroup>
            </Row>

            {data?.details?.comment && (
              <>
                <Separator />
                <Row>
                  <Label>Comment</Label>
                  <ValueGroup>
                    <Value>{data.details.comment}</Value>
                  </ValueGroup>
                </Row>
              </>
            )}
          </Container>
        ) : (
          <Container>
            <Heading>Details</Heading>
            <Separator />

            <Heading>From</Heading>

            {changedFields.length === 0 ? (
              <Row>
                <ValueGroup>
                  <Value>No changes found</Value>
                </ValueGroup>
              </Row>
            ) : (
              changedFields.map(change => (
                <Row key={change.field}>
                  <Label>{formatFieldLabel(change.field)}</Label>
                  <ValueGroup>
                    <Value>{formatFieldValue(change.field, change.before)}</Value>
                  </ValueGroup>
                </Row>
              ))
            )}

            <Separator />

            <Heading>To</Heading>

            {changedFields.length === 0 ? (
              <Row>
                <ValueGroup>
                  <Value>No changes found</Value>
                </ValueGroup>
              </Row>
            ) : (
              changedFields.map(change => (
                <Row key={change.field}>
                  <Label>{formatFieldLabel(change.field)}</Label>
                  <ValueGroup>
                    <Value>{formatFieldValue(change.field, change.after)}</Value>
                  </ValueGroup>
                </Row>
              ))
            )}

            {data?.details?.comment && (
              <>
                <Separator />
                <Row>
                  <Label>Comment</Label>
                  <ValueGroup>
                    <Value>{data.details.comment}</Value>
                  </ValueGroup>
                </Row>
              </>
            )}
          </Container>
        )}
      </>
    </ReactModal>
  );
};
